import * as MUI from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGetTiers } from "../../../../hooks/useGetTiers";
import useClinicListParams from "../hooks/useClinicListParams";
import { CLINIC_STATUS, CLINIC_TYPE_LABELS } from "../utils/clinicFields";
import { countActiveFilters, TEXT_FILTERS } from "../utils/filterClinics";
import DebouncedTextField from "./DebouncedTextField";

const STATUS_LABELS = {
  [CLINIC_STATUS.NEW]: "NewClinic",
  [CLINIC_STATUS.ACTIVE]: "ClinicActive",
  [CLINIC_STATUS.INACTIVE]: "ClinicInactive",
};

function FilterSelect({ label, value, onChange, options }) {
  const { t } = useTranslation();
  return (
    <MUI.TextField
      select
      fullWidth
      size="small"
      label={t(label)}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <MUI.MenuItem value="">{t("All")}</MUI.MenuItem>
      {options.map((option) => (
        <MUI.MenuItem key={option.value} value={option.value}>
          {t(option.label)}
        </MUI.MenuItem>
      ))}
    </MUI.TextField>
  );
}

export default function ClinicListFilters() {
  const { t } = useTranslation();
  const { filters, setFilter, clearFilters } = useClinicListParams();
  const { data: tiers } = useGetTiers();
  const tierOptions = (tiers ?? []).map((tier) => ({
    value: String(tier.id),
    label: tier.title,
  }));
  const typeOptions = Object.entries(CLINIC_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
  const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <MUI.Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "divider" }}>
      <MUI.Grid container spacing={2} alignItems="center">
        {TEXT_FILTERS.map(({ key, label }) => (
          <MUI.Grid item xs={12} sm={6} md={3} key={key}>
            <DebouncedTextField
              fullWidth
              size="small"
              label={t(label)}
              value={filters[key]}
              onCommit={(value) => setFilter(key, value)}
            />
          </MUI.Grid>
        ))}
        <MUI.Grid item xs={12} sm={6} md={3}>
          <FilterSelect
            label="Tier"
            value={filters.tier}
            options={tierOptions}
            onChange={(value) => setFilter("tier", value)}
          />
        </MUI.Grid>
        <MUI.Grid item xs={12} sm={6} md={3}>
          <FilterSelect
            label="ClinicType"
            value={filters.clinicType}
            options={typeOptions}
            onChange={(value) => setFilter("clinicType", value)}
          />
        </MUI.Grid>
        <MUI.Grid item xs={12} sm={6} md={3}>
          <FilterSelect
            label="Status"
            value={filters.status}
            options={statusOptions}
            onChange={(value) => setFilter("status", value)}
          />
        </MUI.Grid>
        <MUI.Grid item xs={12} sm={6} md={3}>
          <MUI.Button disabled={countActiveFilters(filters) === 0} onClick={clearFilters}>
            {t("ClearFilters")}
          </MUI.Button>
        </MUI.Grid>
      </MUI.Grid>
    </MUI.Paper>
  );
}
